import { Haptics, NotificationType } from "@capacitor/haptics"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from 'react-i18next'

import Button from "@/components/common/Button/Button"
import MotionText from "@/components/common/Motion/MotionText"
import MotionDiv from "@/components/common/Motion/MotionDiv"
import HeaderBack from "@/components/dashboard/Header/HeaderBack"
import DecimalInput from "@/components/common/Input/DecimalInput"
import DateInput from "@/components/common/Input/DateInput"

import incomeAPI from "@/services/api/income"
import Input from "@/components/common/Input/Input"

import { IncomeErrors } from "@/types/income"
import { usePageSetup } from "@/hooks/usePageSetup"

export const IncomeCreatePage = () => {
  const { t } = useTranslation();
  usePageSetup(t('income.pageTitle.add'))
  const navigate = useNavigate()

  const [total, setTotal] = useState(0)
  const [errors, setErrors] = useState<IncomeErrors>({})

  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    setIsLoading(true)

    try {
      const form = new FormData(e.currentTarget)

      const date = form.get('date') as string
      const cents = Math.round(total * 100)
      const reference = form.get('reference') as string
      const operation = form.get('operation') as string

      if (cents < 0) {
        setIsLoading(false)
        return
      }

      await incomeAPI.create({
        income_category_code: "other",
        amount_cents: Math.round(total * 100),
        date,
        reference,
        operation
      })

      navigate("/income")

      document.getElementById('root')?.scrollTo(0, 0);
      Haptics.notification({ type: NotificationType.Success })
    } catch (e: any) {
      setErrors({ reference: [t('income.errors.referenceExists')] })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="h-full bg-white flex flex-col">
      <HeaderBack />
      <div className="h-full flex flex-col gap-4 px-5 pb-8">
        <MotionText
          as="h2"
          className="font-semibold text-3xl text-zinc-900 mb-2"
          delay={0.1}
        >
          {t('income.form.addOther')}
        </MotionText>

        <MotionDiv>
          <DecimalInput
            className="w-[100%]"
            inputMode='decimal'
            name='income'
            label={t('income.form.amount')}
            max={9999}
            postfix="\€"
            onChange={value => setTotal(parseFloat(value))}
            required
          />
        </MotionDiv>
        <MotionDiv>
          <Input
            name='operation'
            label={t('income.form.content')}
            required
          />
        </MotionDiv>
        <MotionDiv>
          <DateInput
            inputMode="numeric"
            className="w-[100%]"
            name='date'
            label={t('income.form.date')}
            placeholder={new Date().toISOString().split('T')[0]}
            required
          />
        </MotionDiv>
        <MotionDiv className="pb-[7rem]">
          <Input
            name='reference'
            label={t('income.form.documentReference')}
            required
            error={errors?.reference?.[0]}
          />
        </MotionDiv>
        <div className="flex flex-row gap-2 absolute bottom-0 -ml-5 px-5 pb-8 pt-8 w-full bg-linear-to-t from-white via-white to-transparent from-0% via-40% to-100%">
          <Button type="submit" isLoading={isLoading} className="h-12 w-full">
            <span>{t('common.save')}</span>
          </Button>
        </div>
      </div>
    </form>
  )
}

export default IncomeCreatePage